// src/shared/dates.js
//
// Every way this app writes a date, in one place.
//
//   longDate  "August 25, 2026"  — the body of the agreement, where it reads as prose
//   shortDate "08/25/2026"       — the running page header, where it has a third of a
//                                  column and sits beside the customer and the title
//   isoDate   "2026-08-25"       — file names, so a folder sorts chronologically
//
// Dates reach the renderers in whatever shape they were stored: the raw
// "2026-08-25" from the date input for a fresh draft, or the already-formatted
// "August 25, 2026" from the snapshot of an agreement that was sent months ago.
// Everything here takes either and is a no-op on what it can't read, so an old
// snapshot renders as it always did rather than printing "Invalid Date".
//
// CommonJS + dependency-free like the rest of src/shared — the Node server
// require()s it and the CRA client imports it.

// "2026-08-25" is parsed as local time, not UTC. `new Date("2026-08-25")` is
// midnight UTC, which is the previous day for anyone west of Greenwich — an
// agreement dated the 25th would print as the 24th in New Orleans.
function parseDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const raw = String(value).trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
}

const pad = (n) => String(n).padStart(2, '0');

function shortDate(value) {
  const d = parseDate(value);
  if (!d) return value == null ? '' : String(value);
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

function longDate(value) {
  const d = parseDate(value);
  if (!d) return value == null ? '' : String(value);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function isoDate(value) {
  const d = parseDate(value);
  if (!d) return value == null ? '' : String(value).trim();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

module.exports = { parseDate, shortDate, longDate, isoDate };
